import {
  CSSProperties,
  PropsWithChildren,
  ReactNode,
  RefObject,
  useMemo,
} from 'react';

import { useTheme } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';

import { Header as BackstageHeader } from '@backstage/core-components';

import { HeaderTabsContainer } from './HeaderTabsContainer';
import { NarrowBox, NarrowBoxProps } from './NarrowBox';

export type HeaderProps = PropsWithChildren<{
  title?: ReactNode;
  isLoading?: boolean;
  pageTitleOverride?: string;
  narrow?: boolean | NarrowBoxProps['width'];
  stickyHeader?: boolean;
  stickyHeaderTabs?: boolean;
  headerTabs?: ReactNode;
  headerTabsRef?: RefObject<HTMLDivElement | null>;
  headerBreadcrumbs?: ReactNode;
  enableMargin?: boolean;
  subheader?: ReactNode;
}>;

const useStyles = ({
  stickyHeader,
  paddingTop,
}: Pick<HeaderProps, 'stickyHeader'> & { paddingTop?: number | string }) =>
  useMemo<Record<string, CSSProperties>>(
    () => ({
      root: stickyHeader
        ? {
            position: 'sticky',
            top: 0,
            paddingTop,
          }
        : { paddingTop },
    }),
    [stickyHeader, paddingTop],
  );

export const Header = ({
  title,
  isLoading,
  pageTitleOverride,
  children,
  narrow,
  subheader,
  headerBreadcrumbs = null,
  headerTabs = null,
  stickyHeader = false,
  stickyHeaderTabs = true,
  headerTabsRef,
  enableMargin = false,
}: HeaderProps) => {
  const pageTitleOverrideFallback =
    typeof title === 'string' && title !== '' ? title : 'Confidence';
  const theme = useTheme();
  const styles = useStyles({
    stickyHeader,
    paddingTop: enableMargin ? theme.spacing(2) : undefined,
  });

  const titleElement = isLoading ? (
    <Skeleton height={63} variant="text" width={300} />
  ) : (
    <>
      {headerBreadcrumbs}
      {title}
    </>
  );

  return (
    <>
      <BackstageHeader
        style={styles.root}
        pageTitleOverride={pageTitleOverride ?? pageTitleOverrideFallback}
        title={
          narrow ? (
            <NarrowBox
              width={typeof narrow === 'string' ? narrow : undefined}
              data-testid="header-title"
            >
              {titleElement}
            </NarrowBox>
          ) : (
            titleElement
          )
        }
        subtitle={subheader}
      >
        {children}
      </BackstageHeader>
      {headerTabs && (
        <HeaderTabsContainer
          sticky={stickyHeaderTabs}
          narrow={narrow}
          ref={headerTabsRef}
        >
          {headerTabs}
        </HeaderTabsContainer>
      )}
    </>
  );
};
